/**
 * TenantHierarchyPicker
 *
 * The SAME hierarchical, searchable tenant picker used by the "share agent to
 * tenant" modal (BotShareModal) — depth-indented level icons, a breadcrumb
 * path, and a search box — extracted here as a reusable, controlled component
 * so the Agent-of-Agents simulation panel can offer an identical experience
 * when choosing which tenant to run a simulation under.
 *
 * Styling is reused from BotShareModal.css (the `.searchable-select` /
 * `.select-*` / `.option-*` classes are global), so it looks pixel-identical.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
// Reuse the canonical share-picker styling (global class definitions).
import '../../chatbots/components/BotShareModal.css';

export interface TenantOption {
  tenant_id: string;
  tenant_name: string;
  depth?: number;
  is_root_tenant?: boolean;
  hierarchy_path?: string;
  tenant_code?: string;
  /** True for the target agent's own tenant — surfaced as an "agent home" badge. */
  is_bot_home?: boolean;
}

// Level-based styling — identical to BotShareModal so the two pickers match.
const levelStyles = [
  { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.1)', icon: 'crown' },
  { color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.1)', icon: 'building' },
  { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)', icon: 'folder' },
  { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)', icon: 'users' },
  { color: '#ec4899', bgColor: 'rgba(236, 72, 153, 0.1)', icon: 'user' },
];

function getLevelStyle(depth = 0) {
  return levelStyles[Math.min(Math.max(depth, 0), levelStyles.length - 1)];
}

function getLevelIcon(level: number, color: string) {
  const icons: Record<string, JSX.Element> = {
    crown: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M2 20h20v2H2zm2-2L1 7l5 3 6-8 6 8 5-3-3 13H4z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M3 21h18M9 8h1M9 12h1M9 16h1M14 8h1M14 12h1M14 16h1M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" width="14" height="14" style={{ color }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="7" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
  };
  const style = levelStyles[Math.min(level, levelStyles.length - 1)];
  return icons[style.icon] || icons.user;
}

interface Props {
  tenants: TenantOption[];
  value: string;
  onChange: (tenantId: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export const TenantHierarchyPicker: React.FC<Props> = ({
  tenants, value, onChange, disabled, placeholder,
}) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = useMemo(
    () => tenants.find((t) => t.tenant_id === value) || null,
    [tenants, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q ? tenants : tenants.filter((t) =>
      (t.tenant_name || '').toLowerCase().includes(q) ||
      (t.hierarchy_path || '').toLowerCase().includes(q) ||
      (t.tenant_code || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0));
  }, [tenants, query]);

  const renderInner = (t: TenantOption) => {
    const style = getLevelStyle(t.depth);
    return (
      <>
        <div className="option-avatar tenant" style={{ background: style.bgColor, border: 'none' }}>
          {getLevelIcon(t.depth ?? 0, style.color)}
        </div>
        <div className="option-info">
          <span dir="auto" className="option-name">
            {t.tenant_name}
            {t.is_bot_home ? (
              <span className="option-badge" style={{ background: style.bgColor, color: style.color }}>
                agent home
              </span>
            ) : (
              <span className="option-badge" style={{ background: style.bgColor, color: style.color }} dir="auto">
                {t.is_root_tenant ? 'ROOT' : `Level ${t.depth ?? 0}`}
              </span>
            )}
          </span>
          {t.hierarchy_path ? <span dir="auto" className="option-meta">{t.hierarchy_path}</span> : null}
        </div>
      </>
    );
  };

  return (
    <div className="searchable-select" ref={ref}>
      <button
        type="button"
        className={`select-trigger ${selected ? 'has-value' : ''} ${open ? 'open' : ''}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span dir="auto" className="select-value">
          {selected ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {getLevelIcon(selected.depth ?? 0, getLevelStyle(selected.depth).color)}
              {selected.tenant_name}
            </span>
          ) : (placeholder || 'Choose a tenant…')}
        </span>
        <svg className="select-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="select-dropdown">
          <div className="select-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input dir="auto"
              type="text"
              placeholder={t('common.searchTenants')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </div>
          <div className="select-options">
            {filtered.length === 0 ? (
              <div className="select-no-results">{t('common.noTenantsFound')}</div>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.tenant_id}
                  type="button"
                  className={`select-option ${value === t.tenant_id ? 'selected' : ''}`}
                  onClick={() => { onChange(t.tenant_id); setOpen(false); setQuery(''); }}
                >
                  {renderInner(t)}
                  {value === t.tenant_id && (
                    <svg className="option-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantHierarchyPicker;
